import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { CartContainer, EntityList } from '@aimsos/ui';

import { TRAIT_USES } from '../../constants/intelligence.js';
import { formatTraitDate } from '../../utils/traitDates.js';
import { iconForTraitCategory } from './traitIcons.js';

import { CalendarClock, EyeOff, Hourglass, Quote } from 'lucide-react';

/** A trait used in neither of these is not read by the drafts or by How to approach. */
const DRAFTING_USES = TRAIT_USES.filter((use) => use !== 'next_best_action');

/**
 * One trait in the Traits list (CT-5715), verified or not.
 *
 * `CartContainer` + `EntityList`, the pair every list in this domain uses — not
 * a hand-built card. The trait maps onto `EntityList`'s own slots:
 *
 *   category          -> icon
 *   title             -> title
 *   Said / Noted      -> the badge beside the title (`pinned`)
 *   Re-confirm due    -> status badge
 *   quote             -> description (an unverified trait: its note or confidence)
 *   source, expiry    -> secondaryMeta, plus "Not used in drafts or How to approach"
 *
 * The whole card opens the trait (`onOpen(trait)`, the page's one stable
 * handler); `CartContainer` makes it a keyboard-operable button.
 */
const TraitCard = memo(function TraitCard({ trait, unverified = false, selected = false, onOpen }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const handleOpen = useCallback(() => onOpen?.(trait), [onOpen, trait]);

  const view = useMemo(() => {
    const sourceMeta = trait.sourceLine ? [{ icon: Quote, label: trait.sourceLine }] : [];

    if (unverified) {
      const description =
        trait.note ??
        (trait.confidence != null
          ? t('intelligence.traits.card.lowConfidence', { percent: Math.round(trait.confidence * 100) })
          : undefined);
      return {
        title: trait.title,
        icon: iconForTraitCategory(trait.category),
        iconHighlight: { bgColor: 'Yellow', iconColor: 'Yellow' },
        pinned: { label: t('intelligence.traits.card.unverified'), variant: 'warning' },
        description,
        secondaryMeta: [
          ...sourceMeta,
          ...(trait.disappearsInDays != null
            ? [
                {
                  icon: Hourglass,
                  label: t('intelligence.traits.card.disappears', { count: trait.disappearsInDays }),
                },
              ]
            : []),
        ],
      };
    }

    const expires = formatTraitDate(trait.expiresAt, language);
    const unused = !trait.usedIn.some((use) => DRAFTING_USES.includes(use));

    return {
      title: trait.title,
      icon: iconForTraitCategory(trait.category),
      iconHighlight: { bgColor: 'Purple', iconColor: 'Purple' },
      pinned: trait.sourceKind
        ? { label: t(`intelligence.traits.sourceKind.${trait.sourceKind}`), variant: 'neutral' }
        : undefined,
      status:
        trait.status === 're_confirm_due'
          ? { label: t('intelligence.traits.status.re_confirm_due'), variant: 'warning' }
          : undefined,
      description: trait.quote ? `“${trait.quote}”` : undefined,
      secondaryMeta: [
        ...sourceMeta,
        ...(expires ? [{ icon: CalendarClock, label: t('intelligence.traits.card.expires', { date: expires }) }] : []),
        ...(unused ? [{ icon: EyeOff, label: t('intelligence.traits.card.notUsed') }] : []),
      ],
    };
  }, [trait, unverified, language, t]);

  return (
    <div data-testid="trait-card" data-trait-id={trait.id} data-unverified={unverified || undefined}>
      <CartContainer
        size="S"
        variant="neutral"
        selected={selected}
        onClick={handleOpen}
        aria-label={t('intelligence.traits.card.open', { title: trait.title })}
      >
        <EntityList {...view} />
      </CartContainer>
    </div>
  );
});

export default TraitCard;
